/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useEffect,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';

import { NumberField as SNumberField } from 'sarsaparilla';

/** TODO: We will need to move this logic into the sarsa component */
const NumberField = forwardRef(
    (
        {
            id,
            units,
            visualLabel,
            visualLabelPosition,
            isRequired,
            isInvalid,
            isDisabled,
            errorText,
            shouldClamp,
            isDecreaseDisabled,
            isIncreaseDisabled,
            max,
            min,
            onChange,
            value,
            defaultValue,
            ariaDescribedby,
            className,
            shouldTriggerOnChange,
        },
        ref
    ) => {
        const [state, setState] = useState({
            isInvalid,
            isPristine: !(value || defaultValue),
        });

        const validate = useCallback(() => {
            let invalid = false;
            let validationErrorText = '';
            const currentValue = value || value === 0 ? value : defaultValue;
            const maxTotalMessage = `You can select a maximum of ${max}.`;

            const minTotalMessage = `You must select a minimum of ${min}.`;

            const isMinError = currentValue < min;
            const isMaxError = currentValue > max;
            if (isMinError || isMaxError) {
                validationErrorText = isMinError ? minTotalMessage : maxTotalMessage;
                invalid = true;
            }

            setState((prevState) => ({
                ...prevState,
                isInvalid: invalid,
                isPristine: false,
            }));

            return {
                isInvalid: invalid,
                id,
                errorText: validationErrorText,
            };
        }, [defaultValue, id, max, min, value]);

        useEffect(() => {
            if (isRequired && !state?.isPristine) {
                validate();
            }
        }, [isRequired, state?.isPristine, validate]);

        useImperativeHandle(ref, () => ({
            validate,
        }));

        return (
            <div ref={ref}>
                <SNumberField
                    id={id}
                    units={units}
                    visualLabel={visualLabel}
                    visualLabelPosition={visualLabelPosition}
                    isRequired={isRequired}
                    isInvalid={state?.isInvalid}
                    isDisabled={isDisabled}
                    errorText={errorText}
                    shouldClamp={shouldClamp}
                    isDecreaseDisabled={isDecreaseDisabled}
                    isIncreaseDisabled={isIncreaseDisabled}
                    max={max}
                    min={min}
                    onChange={onChange}
                    value={value}
                    defaultValue={defaultValue}
                    ariaDescribedby={ariaDescribedby}
                    className={className}
                    shouldTriggerOnChange={shouldTriggerOnChange}
                />
            </div>
        );
    }
);

NumberField.propTypes = {
    /** Id for input and label. Required for 508 */
    id: PropTypes.string.isRequired,

    /** Plural descriptor for the number. Used for various accessibility
     * purposes, included the input label (i.e. Number of ${units}) */
    units: PropTypes.string.isRequired,

    /** The text for a visual label. If provided, NumberField is wrapped in
     * a `role="group"` and this label acts as the group's accessibility
     * label */
    visualLabel: PropTypes.string,

    /** If the input is required */
    visualLabelPosition: PropTypes.oneOf(['left', 'top']),

    /** If the input is required */
    isRequired: PropTypes.bool,

    /** Marks and styles the field as invalid */
    isInvalid: PropTypes.bool,

    /** If the input and buttons are disabled */
    isDisabled: PropTypes.bool,

    /** Contents of the error message, usually text */
    errorText: PropTypes.node,

    /** DANGER: setting this prop to false will allow the field to be in an
     * error state, and this component does not self-validate. You MUST
     * handle validation and error state outside the component if you
     * want this behavior. The default of true resets the value to the
     * closest min/max if the user manually enters a value that is out of bounds. */
    shouldClamp: PropTypes.bool,

    /** If the decrease button is disabled. Useful if the sum of a series
     * of NumberFields has a max/min value */
    isDecreaseDisabled: PropTypes.bool,

    /** If the increase button is disabled. Useful if the sum of a series
     * of NumberFields has a max/min value */
    isIncreaseDisabled: PropTypes.bool,

    /** The maximum number allowed in the input */
    max: PropTypes.number,

    /** The minimum number allowed in the input */
    min: PropTypes.number,

    /** Change handler. Always returns a number (not a string) */
    onChange: PropTypes.func,

    /** The input value. Must be a number. Use only if fully controlling
     * NumberField */
    value: PropTypes.number,

    /** The initial value of the input. Must be a number. Use only if *not*
     * fully controlling NumberField */
    defaultValue: PropTypes.number,

    /** Adds an additional aria-describedby id. This is already used to link
     * errorText with the input */
    ariaDescribedby: PropTypes.string,

    /** Additional class added to the outer container */
    className: PropTypes.string,

    /** Trigger onChange in the parent in sync with the NumberFieldInput */
    shouldTriggerOnChange: PropTypes.bool,
};

NumberField.defaultProps = {
    visualLabel: undefined,
    visualLabelPosition: 'left',
    isDisabled: false,
    isRequired: false,
    isInvalid: false,
    errorText: undefined,
    shouldClamp: true,
    isDecreaseDisabled: false,
    isIncreaseDisabled: false,
    value: undefined,
    onChange: undefined,
    defaultValue: 0,
    min: 0,
    max: 99,
    className: undefined,
};

export default NumberField;
